import AppStore from 'fetch/AppFetch';
import LoginFetch from './fetch/LoginFetch';
import ZywaveProxyFetch from './fetch/ZywaveProxyFetch';
import AppConfig from './appConfig';
import {
  ZY_LICENSE_KEY_AMS,
  USER_TYPE_ZYWAVE,
  ZYW_LEADS_REDIRECT_COUNTER,
  ZYW_LEADS_REDIRECT_URL,
} from './lib/config/appConstants';
import { isZywaveUrl } from 'lib/AppHelpers';

// Constants for redirect loop detection
const MAX_REDIRECTS = 3;

/**
 * Class used to verify the user is logged in and can access data. Uses a mock login system for now
 *
 */
class AppAuth {
  static loggedIn = () => {
    return AppStore.fetchUserInfo() !== null;
  };

  /**
   * Logs into the service with the specified email and password, returns a Promise
   *
   * @param email: String
   * @param password: String
   * @returns {Promise.<TResult>}
   */
  static login = async (email, password) => {
    await LoginFetch.login(email, password);
    AppStore.storeToken('');
    return await AppAuth.getUserInfo();
  };

  /**
   * If the user has previously logged in, this will poll the server to validate their token is valid and retrieve
   * the API key needed to make further calls.
   *
   * @returns {Promise.<TResult>}
   */
  static getUserInfo = async () => {
    const json = await LoginFetch.findUserInfo();
    AppStore.storeUserInfo(json);
    return await Promise.resolve();
  };

  /**
   * Logs the user out
   */
  static logout = async () => {
    try {
      // Clear redirect tracking on logout (only if feature is enabled)
      if (AppConfig.enableRedirectZywaveDotCom) {
        AppAuth.clearRedirectTracking();
      }

      AppStore.deleteUserInfo();
      AppStore.deleteZywaveUserFeatureAgencies();

      await LoginFetch.logout();
      AppStore.deleteToken();

      // Open login screen
      if (AppConfig.enableRedirectZywaveDotCom && isZywaveUrl()) {
        window.location.reload();
      } else {
        window.location = AppConfig.logoutUrl;
      }

      return await new Promise(() => {
        setTimeout(function () {});
      });
    } catch {
      // Silently handle logout errors
    }
  };

  /**
   * Fetch zywave feature license via zywave-proxy
   * Note: Agency users are now fetched on-demand via useAgencyUsers hook
   */
  static getZywaveFeatureLicenseAgencies = () => {
    return ZywaveProxyFetch.getFeatureLicense()
      .then((featureLicense) => {
        return {
          has_ams: Object.keys(featureLicense).indexOf(ZY_LICENSE_KEY_AMS) !== -1,
          agency_users: [], // No longer fetched here - lazy loaded in Export dialog
        };
      })
      .catch((error) => {
        // Return default values on error
        console.error('Failed to fetch feature license:', error);
        return {
          has_ams: false,
          agency_users: [],
        };
      });
  };

  /**
   * Checks if user has access to broker search for EB
   * @param {Object} flags - LaunchDarkly flags object from FlagsContext
   * @returns {boolean} whether the user has access to broker search
   */
  static userHasBrokerSearchEb = (flags) => {
    const userInfo = AppStore.fetchUserInfo();
    return !!(userInfo && isZywaveUser(userInfo));
  };

  /**
   * @returns {boolean} whether the user has access to premium contacts or not
   */
  static userHasPremiumContacts = () => {
    const userInfo = AppStore.fetchUserInfo();

    return !!(userInfo && userInfo['zywave_is_premium_contacts'] === 1);
  };

  static userHasCommercialLinesExports = () => {
    const userInfo = AppStore.fetchUserInfo();

    return !!(userInfo && parseInt(userInfo['enable_pc_export_csv']) === 1);
  };

  static userHasEmployeeBenefitsExports = () => {
    const userInfo = AppStore.fetchUserInfo();

    return !!(userInfo && parseInt(userInfo['enable_eb_export_csv']) === 1);
  };

  /**
   * Check if redirect attempts have exceeded the maximum limit to detect infinite redirect loops
   * Only active when enableRedirectZywaveDotCom is true
   * @param {string} url - The URL being redirected to
   * @returns {boolean} - Returns true if max redirects exceeded, false if redirect should proceed
   */
  static hasExceededRedirectLimit = (url) => {
    // Only track redirects if the feature is enabled
    if (!AppConfig.enableRedirectZywaveDotCom) {
      return false;
    }

    const currentUrl = localStorage.getItem(ZYW_LEADS_REDIRECT_URL);
    const redirectCount = parseInt(localStorage.getItem(ZYW_LEADS_REDIRECT_COUNTER) || '0', 10);

    if (currentUrl === url) {
      // Same URL being redirected to again
      const newCount = redirectCount + 1;
      localStorage.setItem(ZYW_LEADS_REDIRECT_COUNTER, newCount.toString());

      if (newCount >= MAX_REDIRECTS) {
        // Max redirects exceeded, clear tracking and return true to indicate limit exceeded
        AppAuth.clearRedirectTracking();
        return true;
      }
    } else {
      // Different URL, reset counter
      localStorage.setItem(ZYW_LEADS_REDIRECT_URL, url);
      localStorage.setItem(ZYW_LEADS_REDIRECT_COUNTER, '1');
    }

    return false;
  };

  /**
   * Clear redirect tracking - call this when navigation succeeds without errors
   * Only active when enableRedirectZywaveDotCom is true
   */
  static clearRedirectTracking = () => {
    // Only clear tracking if the feature is enabled
    if (!AppConfig.enableRedirectZywaveDotCom) {
      return;
    }

    localStorage.removeItem(ZYW_LEADS_REDIRECT_COUNTER);
    localStorage.removeItem(ZYW_LEADS_REDIRECT_URL);
  };

  /**
   * Check if we're in an infinite redirect loop state
   * Only active when enableRedirectZywaveDotCom is true
   * @returns {boolean}
   */
  static isInRedirectLoop = () => {
    // Only check for redirect loops if the feature is enabled
    if (!AppConfig.enableRedirectZywaveDotCom) {
      return false;
    }

    const redirectCount = parseInt(localStorage.getItem(ZYW_LEADS_REDIRECT_COUNTER) || '0', 10);
    return redirectCount >= MAX_REDIRECTS;
  };
}

export const isZywaveUser = (userInfo) => {
  return (
    userInfo?.account_type === USER_TYPE_ZYWAVE ||
    window.location.href.toLowerCase().indexOf('zywave.com') > 0
  );
};

export default AppAuth;
